import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  Avatar,
  Container,
  Paper,
  TextField,
  InputAdornment,
  Alert,
  Divider,
  IconButton,
  Collapse
} from '@mui/material';
import {
  Create,
  Search,
  Visibility,
  Comment,
  Person,
  Announcement,
  Event,
  NewReleases,
  ExpandMore,
  ExpandLess,
  Close,
  EmojiEvents as EmojiEventsIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { storiesAPI } from '../services/api';
import { formatDistanceToNow } from 'date-fns';
import { 
  StoriesGridSkeleton, 
  LoadingSpinner
} from '../components/Loading/LoadingComponents';
import { useAsyncOperation } from '../hooks/useLoading';
import LatestResults from '../components/LatestResults';

const Home = () => {
  const navigate = useNavigate();
  const { isAuthenticated, login } = useAuth();
  const [stories, setStories] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const hasLoadedRef = useRef(false);
  
  // Enhanced loading
  const loading = useAsyncOperation('stories');
  const [announcementsExpanded, setAnnouncementsExpanded] = useState(true);
  const [dismissedAnnouncements, setDismissedAnnouncements] = useState(
    JSON.parse(localStorage.getItem('dismissedAnnouncements') || '[]')
  );

  // Sample announcements - in a real app, these would come from an API or CMS
  const announcements = [
    {
      id: 'welcome-2025',
      type: 'update',
      title: '🎉 Welcome to Short Story Platform!',
      message: 'Share your creativity with our community of writers and readers. Create, comment, and discover amazing stories.',
      date: '2025-08-14',
      priority: 'high',
      action: {
        text: 'Start Writing',
        onClick: () => navigate('/create')
      }
    },
    {
      id: 'commenting-feature',
      type: 'feature',
      title: '💬 Our Goal: Write, Read, Comments & Reply',
      message: 'Engage with stories through our new commenting system! Leave feedback, start discussions, and connect with other writers.',
      date: '2025-08-14',
      priority: 'medium',
      action: {
        text: 'Try It Out',
        onClick: () => navigate('/stories')
      }
    },
    {
      id: 'upcoming-contest',
      type: 'event',
      title: '🏆 Writing Contest',
      message: 'Get ready for our first community writing contest! Login and explore the Theme details.',
      date: '2025-08-20',
      priority: 'medium',
      action: {
        text: isAuthenticated ? 'View Events' : 'Login to Join',
        onClick: () => isAuthenticated ? navigate('/events') : login()
      }
    }
  ];

  // Filter out dismissed announcements
  const activeAnnouncements = announcements.filter(
    announcement => !dismissedAnnouncements.includes(announcement.id)
  );

  const dismissAnnouncement = (announcementId) => {
    const newDismissed = [...dismissedAnnouncements, announcementId];
    setDismissedAnnouncements(newDismissed);
    localStorage.setItem('dismissedAnnouncements', JSON.stringify(newDismissed));
  };

  const getAnnouncementIcon = (type) => {
    switch (type) {
      case 'update': return <NewReleases />;
      case 'feature': return <Announcement />;
      case 'event': return <Event />;
      default: return <Announcement />;
    }
  };

  const getAnnouncementColor = (priority) => {
    switch (priority) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'info';
    }
  };

  const fetchFeaturedStories = useCallback(async () => {
    // Only fetch stories if user is authenticated
    if (!isAuthenticated) {
      setStories([]);
      return;
    }
    
    await loading.execute(
      async () => {
        const response = await storiesAPI.getStories({ limit: 6 });
        setStories(response.data.data.stories || []);
        return response.data;
      },
      {
        phases: ['initial', 'processing'],
        onError: (error) => {
          console.error('Error fetching stories:', error);
          setStories([]);
        }
      }
    );
  }, [isAuthenticated]); // Add isAuthenticated to dependency array

  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true;
      // fetchFeaturedStories();
    } else if (isAuthenticated) {
      // Refetch stories when user logs in
      // fetchFeaturedStories();
    }
  }, [fetchFeaturedStories, isAuthenticated]);

  const handleSearch = () => {
    if (searchTerm.trim()) {
      navigate(`/stories?search=${encodeURIComponent(searchTerm)}`);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <Box>
      {/* Hero Section */}
      <Paper
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          py: 8,
          mb: 6,
          borderRadius: 2,
          alignItems: 'center'
        }}
      >
        <Container maxWidth="md">
          <Box textAlign="center">
            <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
              Share Your Stories
            </Typography>
            <Typography variant="h5" component="p" gutterBottom sx={{ mb: 4, opacity: 0.9 }}>
              A platform for writers to create, share, and discover amazing short stories
            </Typography>
            
            <Box sx={{ mb: 4, maxWidth: 500, mx: 'auto' }}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Search for stories..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={handleKeyPress}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                  sx: {
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    borderRadius: 2
                  }
                }}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              {isAuthenticated ? (
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<Create />}
                  onClick={() => navigate('/create')}
                  sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.3)'
                    }
                  }}
                >
                  Write Your Story
                </Button>
              ) : (
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => login()}
                  sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.3)'
                    }
                  }}
                >
                  Get Started
                </Button>
              )}
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate('/stories')}
                sx={{
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                  color: 'white',
                  '&:hover': {
                    borderColor: 'white',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                  }
                }}
              >
                Browse Stories
              </Button>
            </Box>
          </Box>
        </Container>
      </Paper>

      {/* Announcements Section */}
      {activeAnnouncements.length > 0 && (
        <Container maxWidth="lg" sx={{ mb: 6 }}>
          <Paper
            elevation={2}
            sx={{
              background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
              borderRadius: 2,
              overflow: 'hidden'
            }}
          >
            {/* Header Bar */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 2,
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                cursor: 'pointer',
                borderBottom: announcementsExpanded ? '1px solid rgba(0, 0, 0, 0.1)' : 'none',
                position: 'relative',
                zIndex: 2
              }}
              onClick={() => setAnnouncementsExpanded(!announcementsExpanded)}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Announcement color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  Announcements & Updates
                </Typography>
                <Chip
                  label={activeAnnouncements.length}
                  size="small"
                  color="primary"
                  sx={{ ml: 1 }}
                />
              </Box>
              <IconButton size="small">
                {announcementsExpanded ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
            </Box>

            {/* Collapsible Content */}
            <Collapse in={announcementsExpanded}>
              <Box sx={{ p: 3, pt: 2, position: 'relative', zIndex: 1 }}>
                <Grid container spacing={3}>
                  {activeAnnouncements.map((announcement, index) => (
                    <Grid item xs={12} md={6} lg={4} key={announcement.id}>
                      <Alert
                        severity={getAnnouncementColor(announcement.priority)}
                        icon={getAnnouncementIcon(announcement.type)}
                        sx={{
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          '& .MuiAlert-message': { 
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            flex: 1
                          },
                          '& .MuiAlert-action': { 
                            alignItems: 'flex-start', 
                            pt: 0.5,
                            pl: 0
                          }
                        }}
                        action={
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              dismissAnnouncement(announcement.id);
                            }}
                            sx={{ mt: 0.5 }}
                          >
                            <Close fontSize="small" />
                          </IconButton>
                        }
                      >
                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                            {announcement.title}
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 2, flex: 1 }}>
                            {announcement.message}
                          </Typography>
                          
                          <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            mt: 'auto'
                          }}>
                            {announcement.action && (
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  announcement.action.onClick();
                                }}
                                sx={{ textTransform: 'none' }}
                              >
                                {announcement.action.text}
                              </Button>
                            )}
                            <Typography 
                              variant="caption" 
                              color="text.secondary" 
                              sx={{ ml: announcement.action ? 2 : 0 }}
                            >
                              {new Date(announcement.date).toLocaleDateString()}
                            </Typography>
                          </Box>
                        </Box>
                      </Alert>
                    </Grid>
                  ))}
                </Grid>

                {activeAnnouncements.length > 3 && (
                  <Box sx={{ textAlign: 'center', mt: 3, pt: 2, borderTop: '1px solid rgba(0, 0, 0, 0.1)' }}>
                    <Typography variant="caption" color="text.secondary">
                      💡 Click the × to dismiss announcements you've read
                    </Typography>
                  </Box>
                )}
              </Box>
            </Collapse>
          </Paper>
        </Container>
      )}

      {/* Featured Stories Section - Only for logged-in users */}
      {/* {isAuthenticated && (
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Typography variant="h4" component="h2" gutterBottom sx={{ mb: 3 }}>
            Featured Stories
          </Typography>
          
          {loading.isLoading ? (
            <StoriesGridSkeleton />
          ) : stories.length > 0 ? (
            <Grid container spacing={3}>
              {stories.map((story) => (
                <Grid item xs={12} sm={6} md={4} key={story.story_id}>
                  <Card 
                    sx={{ 
                      height: '100%', 
                      display: 'flex', 
                      flexDirection: 'column',
                      cursor: 'pointer',
                      '&:hover': { transform: 'translateY(-2px)', transition: 'transform 0.2s' }
                    }}
                    onClick={() => navigate(`/stories/${story.story_id}`)}
                  >
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Typography variant="h6" component="h3" gutterBottom>
                        {story.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {story.content.substring(0, 150)}...
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Avatar sx={{ width: 24, height: 24, mr: 1 }}>
                          <Person />
                        </Avatar>
                        <Typography variant="body2" color="text.secondary">
                          {story.author?.given_name} {story.author?.family_name}
                        </Typography>
                      </Box>
                      {story.tags && story.tags.length > 0 && (
                        <Box sx={{ mb: 1 }}>
                          {story.tags.slice(0, 2).map((tag, index) => (
                            <Chip key={index} label={tag} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                          ))}
                        </Box>
                      )}
                    </CardContent>
                    <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Visibility fontSize="small" sx={{ mr: 0.5 }} />
                        <Typography variant="caption">{story.view_count}</Typography>
                        <Comment fontSize="small" sx={{ ml: 1, mr: 0.5 }} />
                        <Typography variant="caption">{story.comment_count}</Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {formatDistanceToNow(new Date(story.created_at), { addSuffix: true })}
                      </Typography>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No stories available yet
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Be the first to share your creativity with the community!
              </Typography>
              <Button
                variant="contained"
                startIcon={<Create />}
                onClick={() => navigate('/create-story')}
              >
                Write Your First Story
              </Button>
            </Paper>
          )}
          
          <Box sx={{ textAlign: 'center', mt: 3 }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/stories')}
              sx={{ textTransform: 'none' }}
            >
              View All Stories
            </Button>
          </Box>
        </Container>
      )} */}

      {/* Latest Contest Results Section */}
      {isAuthenticated ? (
      <LatestResults />
      ): null}
    </Box>
  );
};

export default Home;
